import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Check, ArrowUpRight } from "lucide-react";
import { API } from "../lib/api";
import MarketingLayout from "../components/marketing/MarketingLayout";
import Navbar from "@/components/Navbar";

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/pricing`)
      .then(({ data }) => setPlans(data.plans))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = async (plan) => {
    if (plan.id === "enterprise_custom") {
      navigate(`/contact?intent=demo&product=${encodeURIComponent("Enterprise+")}`);
      return;
    }
    if (!user) { navigate("/auth"); return; }
    setUpgrading(plan.id);
    try {
      await axios.post(`${API}/api/payment/process`, { plan: plan.id }, { withCredentials: true });
      setPaymentSuccess(plan.id);
      if (refreshUser) await refreshUser();
    } catch (err) {
      console.error(err);
    } finally {
      setUpgrading(null);
    }
  };

  // Payment success screen — logged-in users only
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950">
        {user && <Navbar />}
        <div className="max-w-lg mx-auto px-6 py-20">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-10 text-center">
            <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/40 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="font-outfit text-2xl font-black text-slate-900 dark:text-white mb-2">Thanks for your payment!</h2>
            <p className="font-ibm-plex text-sm text-slate-500 mb-2">
              You are now on the <span className="font-bold text-slate-900 dark:text-white capitalize">{paymentSuccess}</span> plan.
            </p>
            <p className="font-ibm-plex text-xs text-slate-400 mb-8">All features for your plan are now unlocked.</p>
            <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard-after-pay"
              className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-outfit font-semibold px-8 py-3 rounded-full hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentPlan = user?.plan || "starter";

  const priceLabel = (plan) =>
    plan.price === null
      ? "Custom"
      : `$${plan.price.toLocaleString()}`;

  const periodLabel = (plan) =>
    plan.period === "custom" ? "quote" : `/ ${plan.period}`;

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-blue-100 dark:bg-blue-950/40 blur-3xl opacity-40" />
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-16 text-center">
          <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-4">Pricing</div>
          <h1 className="font-outfit text-5xl md:text-6xl font-black text-slate-900 dark:text-white leading-[1.05]">
            Simple, employee-tier pricing.
          </h1>
          <p className="mt-6 max-w-2xl mx-auto font-ibm-plex text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            One annual price per tier — no per-seat surprises. Includes every SYNQ product available today.
          </p>
        </div>
      </section>

      <section className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-slate-900 dark:border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {plans.map((plan, idx) => {
                const isPopular = idx === 1; // Growth = popular
                const isCurrent = currentPlan === plan.id;
                const isCustom = plan.price === null;
                return (
                  <div
                    key={plan.id}
                    data-testid={`plan-${plan.id}`}
                    className={`relative bg-white dark:bg-slate-900 rounded-2xl p-6 flex flex-col border-2 transition-colors ${
                      isPopular
                        ? "border-slate-900 dark:border-white ring-2 ring-slate-900 dark:ring-white"
                        : "border-slate-200 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-600"
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-outfit font-bold text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">
                          Popular
                        </span>
                      </div>
                    )}

                    <div className="mb-5">
                      <h3 className="font-outfit text-lg font-black text-slate-900 dark:text-white mb-1">{plan.name}</h3>
                      <p className="font-ibm-plex text-xs text-slate-500 dark:text-slate-400 mb-4">{plan.employee_range}</p>
                      <div className="flex items-end gap-1">
                        <span className="font-outfit text-3xl font-black text-slate-900 dark:text-white leading-none">
                          {priceLabel(plan)}
                        </span>
                        <span className="font-ibm-plex text-xs text-slate-400 mb-1">
                          {isCustom ? "" : periodLabel(plan)}
                        </span>
                      </div>
                      <p className="font-ibm-plex text-[11px] text-slate-400 mt-1">
                        {isCustom ? "Quote for multiple products" : "Billed annually · all-in"}
                      </p>
                    </div>

                    <ul className="space-y-2 mb-6 flex-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-emerald-500 mt-1 flex-shrink-0" />
                          <span className="font-ibm-plex text-xs text-slate-600 dark:text-slate-400 leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      data-testid={`select-plan-${plan.id}`}
                      onClick={() => handleSelect(plan)}
                      disabled={isCurrent || upgrading === plan.id}
                      className={`w-full font-outfit font-semibold py-2.5 rounded-full transition-colors text-sm inline-flex items-center justify-center gap-1.5 ${
                        isCurrent
                          ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50 cursor-default"
                          : isPopular
                            ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100"
                            : "border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
                      } disabled:opacity-70`}
                    >
                      {upgrading === plan.id
                        ? "Processing..."
                        : isCurrent
                          ? "Current Plan"
                          : isCustom
                            ? <>Get quote <ArrowUpRight className="w-3.5 h-3.5" /></>
                            : <>Choose {plan.name}</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-center mt-14">
            <p className="font-ibm-plex text-sm text-slate-500 dark:text-slate-400">
              Includes SYNQ Projects and SYNQ Evolve. Upcoming products (SYNQ AI, Strategy, Risk) will be available under Enterprise+ licenses.
            </p>
            {!user && (
              <Link to="/auth" className="mt-4 inline-block font-outfit text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
                Already have an account? Sign in
              </Link>
            )}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
